import { useState, useEffect } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { getCategories, getPriceObservations } from '../services/api';
import { format } from 'date-fns';

const CHAIN_COLORS = {
  'Kiwi': '#22c55e',
  'Rema 1000': '#ef4444',
  'Coop': '#3b82f6',
  'Meny': '#a855f7',
  'Spar': '#f59e0b',
  'Bunnpris': '#06b6d4',
  'Joker': '#f97316',
};

function colorForChain(chain) {
  return CHAIN_COLORS[chain] || '#94a3b8';
}

export default function CategoryTrendsPage() {
  const [categories, setCategories] = useState([]);
  const [selectedCat, setSelectedCat] = useState('');
  const [observations, setObservations] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getCategories().then((cats) => {
      setCategories(cats);
      if (cats.length > 0) setSelectedCat(String(cats[0].id));
    });
  }, []);

  useEffect(() => {
    if (!selectedCat) return;
    setLoading(true);
    getPriceObservations({ category_id: selectedCat })
      .then(setObservations)
      .finally(() => setLoading(false));
  }, [selectedCat]);

  // Build chart data: group by observed_date, then by chain
  const chains = [...new Set(observations.map((o) => o.store_chain))].filter(Boolean);

  const byDate = {};
  observations.forEach((o) => {
    const d = o.observed_date?.slice(0, 10);
    if (!d) return;
    if (!byDate[d]) byDate[d] = { date: d };
    // Average per chain per date
    if (!byDate[d][`${o.store_chain}_sum`]) {
      byDate[d][`${o.store_chain}_sum`] = 0;
      byDate[d][`${o.store_chain}_count`] = 0;
    }
    byDate[d][`${o.store_chain}_sum`] += Number(o.price_per_canonical_unit);
    byDate[d][`${o.store_chain}_count`] += 1;
  });

  const chartData = Object.values(byDate)
    .map((row) => {
      const out = { date: format(new Date(row.date), 'MMM d') };
      chains.forEach((chain) => {
        if (row[`${chain}_count`]) {
          out[chain] = parseFloat((row[`${chain}_sum`] / row[`${chain}_count`]).toFixed(2));
        }
      });
      return out;
    })
    .sort((a, b) => a.date.localeCompare(b.date));

  const selectedCategory = categories.find((c) => String(c.id) === selectedCat);

  return (
    <div>
      <div className="page-header">
        <div>
          <h2 style={{ marginBottom: '0.25rem' }}>Category price trends</h2>
          <p className="muted" style={{ margin: 0 }}>Price per canonical unit over time, by store chain</p>
        </div>
        <select
          value={selectedCat}
          onChange={(e) => setSelectedCat(e.target.value)}
          style={{ width: 220, maxWidth: '100%' }}
        >
          {categories.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>

      {loading && <p className="muted">Loading…</p>}

      {!loading && chartData.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: '3rem', color: 'var(--color-text-muted)' }}>
          No price data for this category yet. Upload some receipts.
        </div>
      )}

      {!loading && chartData.length > 0 && (
        <div className="card">
          <div style={{ marginBottom: '0.75rem' }}>
            <strong>{selectedCategory?.name}</strong>
            {selectedCategory && <span className="muted"> per {selectedCategory.canonical_unit}</span>}
          </div>
          <ResponsiveContainer width="100%" height={360}>
            <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-chart-grid)" />
              <XAxis dataKey="date" tick={{ fontSize: 12, fill: 'var(--color-chart-axis)' }} />
              <YAxis tick={{ fontSize: 12, fill: 'var(--color-chart-axis)' }} unit=" kr" />
              <Tooltip
                formatter={(value) => [`${value} kr`, '']}
                contentStyle={{
                  fontSize: 13,
                  borderRadius: 6,
                  background: 'var(--color-surface)',
                  border: '1px solid var(--color-border)',
                  color: 'var(--color-text)',
                }}
                labelStyle={{ color: 'var(--color-text-muted)' }}
                itemStyle={{ color: 'var(--color-text)' }}
              />
              <Legend wrapperStyle={{ fontSize: 13, color: 'var(--color-text-muted)' }} />
              {chains.map((chain) => (
                <Line
                  key={chain}
                  type="monotone"
                  dataKey={chain}
                  stroke={colorForChain(chain)}
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
